export type ChangeType = 'New' | 'Improved' | 'Fixed';

export interface ChangelogEntry {
  type: ChangeType;
  title: string;
  description: string;
}

export interface ChangelogRelease {
  version: string;
  date: string;
  summary?: string;
  changes: ChangelogEntry[];
}

export const CHANGELOG: ChangelogRelease[] = [
  {
    version: 'Latest',
    date: '27 August 2026',
    summary:
      'Knowledge Management home redesign, ontology builder full-width layout, GraphRAG scoped to active ontology version, and PDF provenance fix in the viewer.',
    changes: [
      // ── New ────────────────────────────────────────────────────────────────
      {
        type: 'New',
        title: 'Knowledge Management home page',
        description:
          'A dedicated home page now greets users when they open Knowledge Management, providing quick access to Knowledge Bases, ontologies, and the Knowledge Explorer in a single cohesive view.',
      },
      {
        type: 'New',
        title: 'Background ontology generation banner',
        description:
          'After launching an ontology generation job from a Knowledge Base, a banner now appears confirming that generation is running in the background, so you can navigate away without losing context.',
      },
      // ── Improved ──────────────────────────────────────────────────────────
      {
        type: 'Improved',
        title: 'Ontology builder full-width layout and right ribbon',
        description:
          'The ontology editor now expands to fill the full viewport width and uses a collapsible right ribbon for properties and actions, giving the graph canvas significantly more space.',
      },
      {
        type: 'Improved',
        title: 'GraphRAG queries scoped to the active ontology version',
        description:
          'All GraphRAG knowledge-retrieval queries are now scoped to the currently published ontology version (migration 066), preventing stale concepts from earlier versions from surfacing in search results.',
      },
      // ── Fixed ─────────────────────────────────────────────────────────────
      {
        type: 'Fixed',
        title: 'PDF provenance viewer — empty bounding box crash',
        description:
          'The PDF viewer no longer crashes when a located Knowledge Unit has no bounding-box provenance; it now gracefully receives an empty array and renders without highlighting.',
      },
      {
        type: 'Fixed',
        title: 'Graph worker DB pool initialised before consuming',
        description:
          'The graph worker now initialises its database connection pool before it starts consuming Kafka events, preventing a race condition that could cause the first populate event to fail silently.',
      },
    ],
  },
  {
    version: 'v1.3',
    date: '25 August 2026',
    summary:
      'L2/L3 processing moves to a dedicated worker, Co-Scientist UX overhaul with inline citations and hover cards, and per-task model routing for the scientific agent.',
    changes: [
      // ── New ────────────────────────────────────────────────────────────────
      {
        type: 'New',
        title: 'L2/L3 processing migrated to a dedicated worker',
        description:
          'Key-claims extraction and entity-candidate generation now run in the knowledge-workflow service instead of the BFF, enabling recoverable work, bounded model concurrency, and vision processing decided by measurement rather than a static allowlist.',
      },
      {
        type: 'New',
        title: 'Per-task model tier routing in the scientific agent',
        description:
          'The scientific agent now selects between small and large LLM tiers on a per-task basis via AGENT_LLM_TASK_ROLES, reducing cost on lightweight steps while keeping the large model available for complex reasoning.',
      },
      // ── Improved ──────────────────────────────────────────────────────────
      {
        type: 'Improved',
        title: 'Co-Scientist inline citations and Knowledge Unit hover cards',
        description:
          'Citation markers in the Co-Scientist response panel now open the corresponding Knowledge Unit directly, and hovering over a citation shows a rich preview card with the full unit content and provenance.',
      },
      {
        type: 'Improved',
        title: 'Populate and ontology generation moved out of the BFF',
        description:
          'Graph population and ontology generation jobs now run concurrently in the graph-worker process instead of blocking the BFF, improving responsiveness for large knowledge bases.',
      },
      {
        type: 'Improved',
        title: 'L2/L3 status indicator in the document list',
        description:
          'The L2/L3 processing icon now appears directly in the Knowledge Base document list, giving an at-a-glance view of per-document enrichment progress without opening the detail page.',
      },
      // ── Fixed ─────────────────────────────────────────────────────────────
      {
        type: 'Fixed',
        title: 'key_claims and entity_candidates rendering',
        description:
          'The structured-data renderers now correctly read the plain array format produced by the new worker, fixing blank panels after L2/L3 processing.',
      },
      {
        type: 'Fixed',
        title: 'L1 re-embed backfill scoped to short units only',
        description:
          'The L1 embedding backfill no longer re-embeds long units that would be truncated by the model, preventing silent quality regressions on watsonx.',
      },
      {
        type: 'Fixed',
        title: 'Scientific agent evidence formatted for citation navigation',
        description:
          'Evidence blocks emitted by the scientific agent are now formatted so that the UI can resolve citation links correctly.',
      },
    ],
  },
  {
    version: 'v1.2',
    date: '20 August 2026',
    summary:
      'Feature-parity pass following the split of the platform into independent snxt-platform-* service repositories.',
    changes: [
      // ── New ────────────────────────────────────────────────────────────────
      {
        type: 'New',
        title: 'Multi-step ontology generation with a focus option',
        description:
          'For large document sets, ontology generation can now run in multiple passes over per-document extractions instead of the full raw text, and an optional "focus" field lets you narrow the AI\'s attention to a specific sub-topic.',
      },
      // ── Improved ──────────────────────────────────────────────────────────
      {
        type: 'Improved',
        title: 'PDF page and bounding-box provenance',
        description:
          'Knowledge Units extracted from PDF sources now retain the originating page number and bounding box, restoring precise traceability in the provenance viewer.',
      },
      {
        type: 'Improved',
        title: 'Multi-conversation chat history',
        description:
          'The assistant chat panel can now list, restore, and switch between multiple past conversations instead of only ever showing the most recent one.',
      },
      // ── Fixed ─────────────────────────────────────────────────────────────
      {
        type: 'Fixed',
        title: 'Layer-processing status on Knowledge Objects',
        description:
          'The Knowledge Base detail page again reports each document\'s per-layer (L1/L2/L3) processing status correctly.',
      },
    ],
  },
  {
    version: 'v1.1',
    date: '22 July 2026',
    summary:
      'UX improvements — clearer ontology workflows, richer Knowledge Graph context, and better ontology management.',
    changes: [
      // ── New ────────────────────────────────────────────────────────────────
      {
        type: 'New',
        title: 'Visual provenance for AI-generated vs user-edited nodes',
        description:
          'Nodes and edges in the Ontology Editor and Knowledge Graph now display visual provenance badges that distinguish AI-generated concepts from user-added ones and user-corrected ones. Provenance details (author and timestamp) are also shown in the node detail panel.',
      },
      {
        type: 'New',
        title: 'Knowledge Graph legend panel',
        description:
          'A collapsible legend panel on the Knowledge Graph canvas explains every metric and abbreviation (KU, KO, %, etc.). Hovering over a metric badge on a node or edge now shows an inline tooltip with a plain-language definition.',
      },
      // ── Improved ──────────────────────────────────────────────────────────
      {
        type: 'Improved',
        title: 'Explicit Save → Publish → Create KG workflow',
        description:
          'The Ontology Editor now features a visual stepper that makes the mandatory Save → Publish → Create Knowledge Graph pipeline unmistakable. If you attempt to create a KG from an unpublished ontology, a contextual prompt guides you to publish first. Ontology cards on the home page also display clear Draft / Saved / Published status badges.',
      },
      {
        type: 'Improved',
        title: 'Ontology rename and description',
        description:
          'Ontologies can now be renamed at any time — both from the ontology list and inline in the editor. A free-text description field lets you document your methodology, source papers, and any other context to help you and your team understand the ontology later.',
      },
    ],
  },
  {
    version: 'v1.0',
    date: '15 July 2026',
    summary:
      'Major additions across all three modules — Knowledge Base, Ontology Builder, and Knowledge Explorer — plus significant improvements to document processing and search.',
    changes: [
      // ── New ────────────────────────────────────────────────────────────────
      {
        type: 'New',
        title: 'Semantic (vector) search',
        description:
          'Find knowledge by meaning, not just keywords. A new vector search mode lets you search the knowledge graph using natural-language queries.',
      },
      {
        type: 'New',
        title: 'Multi-Knowledge-Base facet search',
        description:
          'The Knowledge Explorer facet-search panel now spans all your Knowledge Bases at once, so cross-domain discoveries are just one query away.',
      },
      {
        type: 'New',
        title: 'Schema suggestion from a concept',
        description:
          'Right-click any node in the graph to ask the AI to suggest an ontology schema rooted at that concept — great for expanding a specific branch of your ontology.',
      },
      {
        type: 'New',
        title: 'Dataset extraction pipeline v2 with AI schema suggestions',
        description:
          'A redesigned dataset builder pipeline now proposes an extraction schema automatically, making it much faster to go from raw documents to structured datasets.',
      },
      {
        type: 'New',
        title: 'Document image extraction and PDF provenance viewer',
        description:
          'Images are now extracted from converted documents and stored alongside the text. A new PDF provenance viewer lets you trace any Knowledge Unit back to its exact location in the source PDF.',
      },
      {
        type: 'New',
        title: 'A-Box concept instances in Knowledge Explorer',
        description:
          'The Knowledge Explorer now displays individual concept instances from the A-Box (knowledge) graph alongside T-Box ontology nodes, giving you a complete picture of your knowledge.',
      },
      {
        type: 'New',
        title: 'Knowledge Unit detail flyout',
        description:
          'Click any Knowledge Unit in the explorer or evidence panel to open a rich detail flyout showing its full content, metadata, and source provenance.',
      },
      // ── Improved ──────────────────────────────────────────────────────────
      {
        type: 'Improved',
        title: 'Ontology save & publish flow',
        description:
          'The save and publish dialog now shows a snapshot preview of the ontology version being committed, with clearer button states and feedback during the operation.',
      },
      {
        type: 'Improved',
        title: 'Version & status badges on the welcome page',
        description:
          'Each ontology card on the home page now shows a version badge and publish status at a glance. An expandable version history lets you review past snapshots without leaving the page.',
      },
      {
        type: 'Improved',
        title: 'Knowledge Unit handling in the dataset builder',
        description:
          'Table-based Knowledge Units are now processed more accurately, and stale extracted rows are automatically purged when a document is reprocessed.',
      },
      {
        type: 'Improved',
        title: 'On-demand document reconversion',
        description:
          'Documents can now be reconverted at any time from the Knowledge Base detail page, and in-document image references are correctly rewritten in the resulting markdown.',
      },
      {
        type: 'Improved',
        title: 'Knowledge Explorer layout controls',
        description:
          'New layout controls in the Knowledge Explorer let you switch between force-directed, hierarchical, and radial graph layouts to best suit your exploration style.',
      },
      // ── Fixed ─────────────────────────────────────────────────────────────
      {
        type: 'Fixed',
        title: 'Robust ontology generation for large AI responses',
        description:
          'A JSON repair utility now recovers truncated or malformed AI responses during ontology generation, preventing silent failures on large inputs.',
      },
      {
        type: 'Fixed',
        title: 'Image loading errors in local development',
        description:
          'Resolved 429 (rate-limit) errors that caused document images to fail to load when running the platform locally.',
      },
    ],
  },
];
